import StaticPage, { Section } from "../components/StaticPage";
import { Link } from "react-router-dom";
import { BookOpen, Leaf, Flame, Beaker, Cookie, Wind, Scale, Heart } from "lucide-react";

const TOPICS = [
    {
        slug: "strain-types",
        icon: Leaf,
        title: "Strain types",
        body: "Indica, sativa, hybrid — the three labels you'll see on every shelf. We break down what each really means, how much of it is marketing, and how to read terpene profiles instead.",
        cta: { label: "Browse strains", href: "/strains" },
    },
    {
        slug: "terpenes",
        icon: Flame,
        title: "Terpenes & the entourage effect",
        body: "Why one sativa puts you to sleep and another wakes you up. A short guide to the seven terpenes that matter most — limonene, myrcene, caryophyllene, pinene, linalool, terpinolene, and humulene.",
    },
    {
        slug: "dosing",
        icon: Scale,
        title: "How to dose",
        body: "Start low, go slow — but what does that actually mean? Per-format dosing math for flower, vapes, edibles, and tinctures. Includes a microdose primer for daytime users.",
    },
    {
        slug: "formats",
        icon: Wind,
        title: "Consumption formats",
        body: "Flower vs. vape vs. edible vs. tincture vs. topical. Onset times, duration, discretion, and which format fits which moment.",
    },
    {
        slug: "edibles-101",
        icon: Cookie,
        title: "Edibles 101",
        body: "Why edibles hit different. How your liver converts THC to 11-hydroxy-THC, why timing matters, and how to safely titrate from 2.5 mg to a full session dose.",
    },
    {
        slug: "concentrates",
        icon: Beaker,
        title: "Concentrates explained",
        body: "Live resin, live rosin, hash rosin, distillate, sauce, badder, budder. What each is, how each is made, and which one's worth the markup for what you're after.",
    },
    {
        slug: "wellness",
        icon: Heart,
        title: "Cannabis & wellness",
        body: "Sleep, anxiety, pain, appetite, focus. The non-medical use cases the plant is most commonly used for in NY, and the formats we'd point you toward for each.",
    },
    {
        slug: "ny-law",
        icon: BookOpen,
        title: "NY cannabis law",
        body: "Adult-use legality, possession limits (3 oz flower / 24 g concentrate), where you can consume, where you can't, and how to verify any licensed operator via the OCM.",
    },
];

export default function Learn() {
    return (
        <StaticPage
            title="Learn · Cannabis Education · ZenZest"
            description="Plain-English cannabis education from the team at ZenZest. Strain types, terpenes, dosing, formats, edibles, concentrates, wellness, and NY cannabis law."
            canonical="https://weeddispensarynearme.com/learn"
            eyebrow="Learn"
            h1="Cannabis, explained without the marketing."
            intro="Plain-English answers to the questions our staff hear at the counter every day. Written by our buyers and educators — no upsell, no hype."
            cta={[
                { label: "Shop the menu", href: "/menu/queens-long-island", primary: true },
                { label: "Ask in store", href: "/queens/contact" },
            ]}
        >
            <Section id="topics" title="Topics">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 not-prose">
                    {TOPICS.map((t) => (
                        <div
                            key={t.slug}
                            data-testid={`learn-topic-${t.slug}`}
                            className="p-5 bg-zen-surface border border-zen-line"
                        >
                            <t.icon size={20} className="text-zen-olive mb-3" />
                            <h3 className="font-serif text-xl text-zen-ink font-light leading-tight mb-2">
                                {t.title}
                            </h3>
                            <p className="text-sm text-zen-ink-soft leading-relaxed mb-3">
                                {t.body}
                            </p>
                            {t.cta && (
                                <Link
                                    to={t.cta.href}
                                    className="text-[11px] uppercase tracking-[0.22em] text-zen-olive border-b border-zen-olive/40 pb-0.5 inline-block"
                                >
                                    {t.cta.label}
                                </Link>
                            )}
                        </div>
                    ))}
                </div>
            </Section>

            <Section id="philosophy" title="Our take">
                <p>
                    You shouldn&rsquo;t need to translate a menu to know what
                    you&rsquo;re buying. Our shelves are organized by effect and onset
                    as much as by category — and our buyers can talk through any SKU
                    in human English. If you&rsquo;re ever confused, that&rsquo;s our
                    fault, not yours.
                </p>
                <p>
                    The content above is written by the same people who curate the
                    menu, not a freelance marketing team. We&rsquo;ll update it as
                    formulations and the science move, and we&rsquo;ll always cite the
                    studies we&rsquo;re leaning on.
                </p>
            </Section>
        </StaticPage>
    );
}
